import { useEffect, useMemo, useState } from 'react';
import './UserProfile.css';
import { getUserAssets, handleRequestError, IUser, Logger, Tabs, toast, postUserCount, useUserEmail, useFeatureIsEnabled } from '@desp-aas/desp-ui-fwk';
import { Route, Routes, useNavigate } from 'react-router-dom';
import UserProfileAvatar from './UserProfileAvatar/UserProfileAvatar';
import UserAssets from './UserAssets';
import { UserBookmarks } from './UserBookmarks';
import { IProfileAssets } from './UserProfileAssets';
import { UserAccount } from './UserAccount';

const logger = new Logger("component", "userProfile");

type IProps = {
    user: IUser
    userPage?: boolean
}

export function UserProfile(props: IProps) {
    const [assets, setAssets] = useState<IProfileAssets>({});
    const [loadingAssets, setLoadingAssets] = useState(false);
    const [loadingPosts, setLoadingPosts] = useState(false);
    const [posts, setPosts] = useState<number|undefined>(undefined);
    const isEmailEnabled = useFeatureIsEnabled("email_enabled");
    const navigate = useNavigate();

    const currentEmail = useUserEmail();

    const tabs = useMemo(() => {
        return [
            { label: "Account", path: "/user/account"  },
            { label: "Assets", path: "/user/assets"  },
            { label: "Bookmarks", path: "/user/bookmarks"  },
        ]
    }, [ props.userPage ]);

    const fetchAssets = async () => {
        setLoadingAssets(true)
        try {
            let _assets = await getUserAssets(props.user.id)
            logger.debug("fetched assets?", _assets, props.user)
            let assets: IProfileAssets = {}
            for (let asset of _assets) {
                if (asset.type in assets)
                    assets[asset.type].push(asset)
                else assets[asset.type] = [asset]
            }
            logger.debug("assets?", assets)
            setAssets(assets);
        }
        catch (e) {
            logger.error("Error during asset fetch", e);
            if(!props.userPage){
                toast(<>User not found</>, { type: "warning" })
                navigate('/')
            }
        }
        finally {
            setLoadingAssets(false)
        }
    }
    const fetchPosts = async () => {
        setLoadingPosts(true)
        try {
            setPosts(await postUserCount(props.user.id));
        }
        catch (e) {
            logger.error("Error during asset fetch", e);
            handleRequestError(e, { defaultMessage: "An error has occured during post retrieval" })
        }
        finally {
            setLoadingPosts(false)
        }
    }

    useEffect(() => {
        fetchAssets();
        fetchPosts();
    }, [ props.user ]);


    return <div className="user-profile">
            <div className="user-profile-information">
                <div className="user-profile-image-container">
                    <UserProfileAvatar user={props.user} userPage={props.userPage}/>
                </div>
                <div className="user-profile-section user-profile-names">
                    <div className="user-profile-name">
                        {props.user.username}
                        {/* {props.user.displayName} */}
                    </div>
                    {/* <div className="user-profile-username">
                        {props.user.username}
                    </div> */}
                    {
                        props.userPage && isEmailEnabled &&
                        <>
                            {
                                currentEmail
                                ?
                                <>
                                    <div className="user-profile-email">
                                        {currentEmail}
                                    </div>
                                </>
                                : <>
                                    <div className="user-profile-email">
                                        Not set yet.
                                    </div>
                                </>
                            }
                        </>
                    }
                </div>
                <div className="user-profile-section user-profile-asset-info-list">
                    {
                        [
                            { label: <>Datasets</>, type: 'dataset' },
                            { label: <>Models</>, type: 'model' },
                            { label: <>Applications</>, type: 'application' },
                        ]
                        .map((r, idx)=>(
                            <div className="user-profile-asset-info" key={r.type}>
                                <label>{r.label}</label>
                                <p>{ loadingAssets ?  <div className="user-profile-asset-info-loading loading"/> : assets[r.type]?.length || 0 }</p>
                            </div>
                        ))
                    }
                </div>
                <div className="user-profile-section user-profile-asset-info-list">
                    {
                        [
                            { label: <>Documents</>, type: 'paper' },
                            { label: <>Courses</>, type: 'course' },
                        ]
                        .map((r, idx)=>(
                            <div className="user-profile-asset-info" key={r.type}>
                                <label>{r.label}</label>
                                <p>{ loadingAssets ? <div className="user-profile-asset-info-loading loading"/> : assets[r.type]?.length || 0 }</p>
                            </div>
                        ))
                    }
                    {
                            <div className="user-profile-asset-info">
                                <label>Posts</label>
                                <p>{ loadingPosts ? <div className="user-profile-asset-info-loading loading"/> : posts || 0 }</p>
                            </div>
                    }
                </div>
                
            </div>
            <div id="user-profile-container">
                {
                    props.userPage &&
                    <Tabs
                        tabs={tabs}
                    />
                }
                <div id="user-profile-content" className="simple-scrollbar">
                    <Routes>
                        <Route path={props.userPage ? "/assets" : "/"}
                            element={
                                <UserAssets
                                    assets={assets}
                                    loadingAssets={loadingAssets}
                                    fetchAssets={fetchAssets}
                                />  
                            }
                        />
                        
                        {
                            props.userPage &&
                            <>
                                <Route
                                    path='/account'
                                    element={<UserAccount/>}
                                />
                                <Route
                                    path='/bookmarks'
                                    element={<UserBookmarks/>}
                                />
                            </>
                        }
                        <Route
                            path='*'
                            element={
                                <div className="no-data">
                                    Page not found
                                </div>
                            }
                        />

                    </Routes>
                </div>
            </div>
    </div>
};
export default UserProfile;
